import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { type Clock } from '#packages/data-collection/services/clock';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type Audit,
  type NewAudit,
} from '#packages/data-collection/models/audit';
import { type MutationPageState } from '#packages/data-collection/types/page-state';

import { useCreateAuditMutation } from '#apps/client-web/features/audit/hooks/use-create-audit-mutation';
import { routes } from '#apps/client-web/app/routes';
import { AUDITS_QUERY_KEY } from '#apps/client-web/features/audit/types/react-query';
import { type AuditService } from '#apps/client-web/features/audit/services/audit';

export type AuditCreatePageViewModel = ReturnType<
  typeof useAuditCreatePageViewModel
>;

type AuditCreatePageViewModelProps = LoggingContext & {
  auditService: AuditService;
  queryClient: QueryClient;
  clock: Clock;
};

export const useAuditCreatePageViewModel = ({
  auditService,
  queryClient,
  clock,

  logger,
}: AuditCreatePageViewModelProps) => {
  const navigate = useNavigate();

  const form = useForm<NewAudit>({
    defaultValues: {
      title: `Audit - ${clock.now().format('l LT')}`,
      types: ['ea'],
    },

    reValidateMode: 'onChange',
  });

  const createAuditMutation = useCreateAuditMutation({
    auditService,
    queryClient,
  });

  const onCreate = useCallback(
    (newAudit: NewAudit) => {
      logger?.debug({ newAudit }, 'Creating new audit');

      createAuditMutation.mutate(
        { newAudit },
        {
          onSuccess: (createdAudit: Audit) => {
            logger?.debug(
              { auditId: createdAudit.id },
              'Audit created successfully',
            );

            // invalidate audit list queries
            queryClient
              .invalidateQueries({
                queryKey: [AUDITS_QUERY_KEY],
              })
              .then(() => {
                logger?.debug('Audit list queries invalidated');

                logger?.debug(
                  { auditId: createdAudit.id },
                  'Navigating to audit details page',
                );

                // navigate to audit details page
                navigate(routes.audit.getDetailsPath(createdAudit.id), {
                  replace: true,
                });
              });
          },

          onError: (err) => {
            logger?.error({ err }, 'Failed to create new audit');
            throw err;
          },
        },
      );
    },
    [createAuditMutation.mutate, navigate],
  );

  // page state synchronization
  const pageState = useMemo<MutationPageState>(() => {
    if (createAuditMutation.isIdle) {
      return { state: 'idle' };
    }

    if (createAuditMutation.isPending) {
      return { state: 'pending' };
    }

    if (createAuditMutation.isError) {
      return { state: 'error', error: createAuditMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    createAuditMutation.isIdle,
    createAuditMutation.isPending,
    createAuditMutation.isError,
    createAuditMutation.error,
  ]);

  return {
    form,
    page: pageState,

    onSubmit: form.handleSubmit(onCreate),
  };
};
